import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const after = loc.state?.from || "/app/products";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav(after, { replace: true });
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4">
      <div className="w-full max-w-sm ap-panel p-6" data-testid="login-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-9 w-9 rounded-md bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Flame className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">AffiliatePilot</h1>
            <div className="text-[11px] mono text-zinc-500">operator login</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-zinc-300">Email</Label>
            <Input
              id="email" type="email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-300">Password</Label>
            <Input
              id="password" type="password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 bg-zinc-900 border-zinc-700 text-zinc-100"
              data-testid="login-password-input"
            />
          </div>
          {error && (
            <div className="text-xs text-amber-300 bg-amber-950/30 border border-amber-900/60 rounded-md p-2" data-testid="login-error">
              {error}
            </div>
          )}
          <Button
            type="submit" disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
